import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const MANAGER_ROLES = ["administrator", "head_coach", "coach", "manager"];

function roleToLabel(role: string): string {
  if (role === "coach" || role === "head_coach") return "Coach";
  return "Manager";
}

/**
 * One-shot check: does the current user hold an active manager-level role
 * in club_central_user_team_roles?
 */
export async function hasActiveCCTeamRole(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from("club_central_user_team_roles")
    .select("id")
    .eq("user_id", userId)
    .in("role", MANAGER_ROLES)
    .eq("status", "active")
    .limit(1);

  return !!data && data.length > 0;
}

/**
 * Team-scoped check: does the current user hold an active manager-level role
 * for a specific TeamVote team (resolved via club_central_team_mappings)?
 * Returns the matched role so callers can distinguish coach vs manager.
 */
export async function hasActiveCCTeamRoleForTeam(
  userId: string,
  teamvoteTeamId: string
): Promise<{ hasRole: boolean; role: string | null }> {
  // 1. Resolve the CC team ID from the mapping
  const { data: mapping } = await supabase
    .from("club_central_team_mappings")
    .select("club_central_team_id")
    .eq("teamvote_team_id", teamvoteTeamId)
    .limit(1)
    .maybeSingle();

  if (!mapping) return { hasRole: false, role: null };

  // 2. Check if user has an active role for that CC team
  const { data: roleData } = await supabase
    .from("club_central_user_team_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("team_id", mapping.club_central_team_id)
    .in("role", MANAGER_ROLES)
    .eq("status", "active")
    .limit(1);

  if (roleData && roleData.length > 0) {
    return { hasRole: true, role: roleData[0].role };
  }
  return { hasRole: false, role: null };
}

/**
 * React hook wrapper – resolves the current user's CC team role status and label.
 */
export function useCCTeamRole() {
  const [hasCCRole, setHasCCRole] = useState(false);
  const [ccRoleLabel, setCCRoleLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || cancelled) { setLoading(false); return; }

        const { data } = await supabase
          .from("club_central_user_team_roles")
          .select("role")
          .eq("user_id", session.user.id)
          .in("role", MANAGER_ROLES)
          .eq("status", "active")
          .limit(1);

        if (!cancelled && data && data.length > 0) {
          setHasCCRole(true);
          setCCRoleLabel(roleToLabel(data[0].role));
        }
      } catch {
        // silently ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    check();
    return () => { cancelled = true; };
  }, []);

  return { hasCCRole, ccRoleLabel, loading };
}
